import Head from 'next/head';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { PortableText } from '@portabletext/react';

import {
  buildArticleDocumentPath,
  buildSanityImageUrl,
  getAllArticlePageSlugsQuery,
  getArticlePage,
  sanityClient
} from '@lib/sanity';
import type { SanityArticlePage } from '@lib/sanity';
import { FFH_HOSTNAME } from '@lib/constants';
import {
  buildArticleBreadcrumbs,
  buildArticleJsonLd,
  buildArticleMetaTitle,
  FACEBOOK_PAGE_URL,
  PREMIUM_BODY_CLASS_NAME,
  resolvePublishedAt,
  scaleShareImageDimensions,
  SITE_TWITTER_HANDLE,
  toTwitterHandle
} from '@lib/article-seo';
import {
  buildBreadcrumbJsonLd,
  serialiseJsonLd
} from '@lib/page-utils/structured-data';
import { buildArticlePortableTextComponents } from '@components/article/portable-text-components';
import { ArticleHeader } from '@components/article/ArticleHeader';
import { ArticleRail } from '@components/article/ArticleRail';
import { AuthorCard } from '@components/article/AuthorCard';
import { PremiumBody } from '@components/article/PremiumBody';
import { ReadingProgressBar } from '@components/article/ReadingProgressBar';
import { ShareRow } from '@components/article/ShareRow';
import { TableOfContents } from '@components/article/TableOfContents';
import {
  countBodyWords,
  estimateReadMinutes,
  extractHeadings
} from '@lib/article-blocks/headings';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import WideLayout from '@components/layout/wide';
import { MissingPropsRecovery } from '@components/missing-props-recovery';
import { usePremiumBody } from '@hooks/usePremiumBody';

// Time-based ISR until the Sanity publish webhook (FFH-2627) lands. Shared
// with the team reveals route, which renders the same template.
export const REVALIDATE_SECONDS = 600;
// An unknown slug can be a publish race, so the 404 must expire rather than
// cache until the next deploy.
export const NOT_FOUND_REVALIDATE_SECONDS = 60;
const OG_IMAGE_WIDTH = 1200;

const PROPS_RELOAD_MARKER_KEY = 'ffh-article-page-props-reload';
const MISSING_PROPS_RUM_CONTEXT = 'article-page-missing-props';
const ARTICLES_FALLBACK_PATH = '/';

export type ArticlePageProps = {
  /**
   * Always supplied by getStaticProps, but a client-side navigation can
   * render the page without it when the _next/data fetch fails, so the
   * component must tolerate its absence. See MissingPropsRecovery.
   */
  page?: SanityArticlePage;
  blockData: ArticleBlockData;
  isPreview?: boolean;
};

export const ArticlePage = ({
  page,
  blockData,
  isPreview = false
}: ArticlePageProps) => {
  // The preview carries the draft's premium body; a published page never does,
  // so an entitled member fetches it here after the page loads.
  const premiumBodyState = usePremiumBody(isPreview ? undefined : page);

  if (!page) {
    return (
      <MissingPropsRecovery
        context={MISSING_PROPS_RUM_CONTEXT}
        reloadMarkerKey={PROPS_RELOAD_MARKER_KEY}
        fallbackPath={ARTICLES_FALLBACK_PATH}
        errorMessage="Article page props missing"
      />
    );
  }

  const metaTitle = buildArticleMetaTitle(page);
  const metaDescription = page.seo?.metaDescription || page.standfirst;
  const documentPath = buildArticleDocumentPath(page);
  const canonicalUrl =
    page.seo?.canonicalUrl || `${FFH_HOSTNAME}${documentPath}`;
  const shareImage = page.seo?.ogImage ?? page.featuredImage;
  const ogImageUrl = shareImage
    ? buildSanityImageUrl(shareImage).width(OG_IMAGE_WIDTH).url()
    : undefined;
  const shareImageDimensions = scaleShareImageDimensions(
    OG_IMAGE_WIDTH,
    shareImage?.asset?.metadata?.dimensions
  );
  const publishedAt = resolvePublishedAt(page);
  const isPremium = page.accessLevel === 'premium';
  // The static page only holds the free half, so a premium read time would
  // always say one minute.
  const readMinutes = isPremium ? undefined : estimateReadMinutes(page.body);
  const authorHandle = page.author?.twitterHandle
    ? toTwitterHandle(page.author.twitterHandle)
    : undefined;
  const structuredData = isPreview
    ? []
    : [
        {
          key: 'jsonld-article',
          data: buildArticleJsonLd({
            page,
            canonicalUrl,
            description: metaDescription,
            image: ogImageUrl
              ? { url: ogImageUrl, ...shareImageDimensions }
              : undefined,
            wordCount: countBodyWords(page.body)
          })
        },
        {
          key: 'jsonld-breadcrumbs',
          data: buildBreadcrumbJsonLd(buildArticleBreadcrumbs(page))
        }
      ];

  const premiumBody =
    premiumBodyState.status === 'ready'
      ? premiumBodyState.body
      : isPreview
      ? page.premiumBody
      : undefined;

  // Headings are extracted across both halves at once so ids stay unique and
  // the table of contents covers the whole piece once the premium body lands.
  const headings = extractHeadings([...page.body, ...(premiumBody ?? [])]);
  const freeHeadingCount = extractHeadings(page.body).length;
  const premiumHeadings = headings.slice(freeHeadingCount);
  const showTableOfContents = page.showTableOfContents ?? true;
  const showRail = page.showRail ?? true;
  // Ids are consumed as headings render, so each map gets its own copy.
  const components = buildArticlePortableTextComponents(blockData, [
    ...headings
  ]);

  return (
    <>
      <Head>
        <title>{metaTitle}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta property="og:url" content={canonicalUrl} key="og:url" />
        {metaDescription && (
          <meta
            property="og:description"
            content={metaDescription}
            key="og:description"
          />
        )}
        {ogImageUrl && (
          <meta property="og:image" content={ogImageUrl} key="og:image" />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:width"
            content={String(shareImageDimensions.width)}
            key="og:image:width"
          />
        )}
        {shareImageDimensions && (
          <meta
            property="og:image:height"
            content={String(shareImageDimensions.height)}
            key="og:image:height"
          />
        )}
        <meta property="og:type" content="article" key="og:type" />
        {publishedAt && (
          <meta
            property="article:published_time"
            content={publishedAt}
            key="article:published_time"
          />
        )}
        {(page._updatedAt || publishedAt) && (
          <meta
            property="article:modified_time"
            content={page._updatedAt ?? publishedAt}
            key="article:modified_time"
          />
        )}
        <meta
          property="article:publisher"
          content={FACEBOOK_PAGE_URL}
          key="article:publisher"
        />
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        <meta
          name="twitter:site"
          content={SITE_TWITTER_HANDLE}
          key="twitter:site"
        />
        <meta name="twitter:title" content={metaTitle} key="twitter:title" />
        {metaDescription && (
          <meta
            name="twitter:description"
            content={metaDescription}
            key="twitter:description"
          />
        )}
        {ogImageUrl && (
          <meta name="twitter:image" content={ogImageUrl} key="twitter:image" />
        )}
        {authorHandle && (
          <meta
            name="twitter:creator"
            content={authorHandle}
            key="twitter:creator"
          />
        )}
        {(isPreview || page.seo?.isHiddenFromSearch) && (
          <meta name="robots" content="noindex,nofollow" />
        )}
        {structuredData.map(({ key, data }) => (
          <script
            type="application/ld+json"
            key={key}
            dangerouslySetInnerHTML={{ __html: serialiseJsonLd(data) }}
          />
        ))}
      </Head>
      <ReadingProgressBar />
      <WideLayout>
        {isPreview && (
          <div
            className="bg-warning-300 px-4 py-2 text-center text-sm font-semibold text-neutral-900"
            data-cy="article-preview-banner"
          >
            Draft preview. Publish in the Studio to make this article live.
          </div>
        )}
        <div className="bg-white">
          <div
            className={`mx-auto max-w-[1240px] px-4 pb-20 pt-10 sm:px-8 sm:pt-14 ${
              showRail
                ? 'lg:grid lg:grid-cols-[minmax(0,1fr)_316px] lg:items-start lg:gap-16'
                : ''
            }`}
          >
            <article
              className={`article w-full max-w-[700px] ${
                showRail ? '' : 'mx-auto'
              }`}
            >
              <ArticleHeader page={page} readMinutes={readMinutes} />
              {showTableOfContents && <TableOfContents headings={headings} />}
              <div className="content-body">
                <PortableText value={page.body} components={components} />
              </div>
              {isPreview && page.premiumBody && (
                <div
                  className={`content-body ${PREMIUM_BODY_CLASS_NAME}`}
                  data-cy="article-premium-body"
                >
                  <div
                    className="my-8 border-t border-dashed border-warning-300 pt-2 text-center text-xs font-semibold uppercase tracking-[0.1em] text-neutral-500"
                    data-cy="article-premium-preview-divider"
                  >
                    Premium body, members only
                  </div>
                  <PortableText
                    value={page.premiumBody}
                    components={buildArticlePortableTextComponents(blockData, [
                      ...premiumHeadings
                    ])}
                  />
                </div>
              )}
              <PremiumBody
                state={premiumBodyState}
                headings={premiumHeadings}
                returnTo={documentPath}
              />
              <ShareRow url={canonicalUrl} title={page.title} />
              {page.author && (
                <AuthorCard
                  author={page.author}
                  bio={
                    page._type === 'teamReveal'
                      ? page.author.teamRevealBio ?? page.author.bio
                      : page.author.bio
                  }
                />
              )}
            </article>
            {showRail && <ArticleRail headings={headings} />}
          </div>
        </div>
      </WideLayout>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityClient.fetch<{ slug: string }[]>(
    getAllArticlePageSlugsQuery
  );

  return {
    paths: slugs
      .filter((entry) => entry.slug)
      .map((entry) => ({ params: { slug: entry.slug } })),
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps<ArticlePageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  const page = await getArticlePage(slug);

  if (!page) {
    return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
  }

  // Only the free body is hydrated here: the published query never projects
  // the premium body, so a static payload can never carry it.
  const blockData = await hydrateArticleBlocks(page.body);

  return {
    props: { page, blockData },
    revalidate: REVALIDATE_SECONDS
  };
};

export default ArticlePage;
